import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/feature/DashboardLayout';
import ChildrenTab from './components/ChildrenTab';
import AdultsTab from './components/AdultsTab';
import { useAuth, api } from '@/lib/api';
import { getPointsIcon } from '@/lib/utils/pointsIcon';

const tabs = [
  { id: 'children', label: '儿童管理', icon: 'ri-user-smile-fill' },
  { id: 'adults', label: '家长管理', icon: 'ri-parent-fill' }
];

export default function MembersPage() {
  const { currentFamily, updateCurrentFamilyName } = useAuth();
  const [activeTab, setActiveTab] = useState('children');
  const [showFamilySettings, setShowFamilySettings] = useState(false);
  const [familySettings, setFamilySettings] = useState({
    name: '',
    pointName: '星星',
    exchangeRatio: 5
  });
  const [tempSettings, setTempSettings] = useState(familySettings);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canManage = currentFamily?.my_role === 1;

  // 加载家庭信息
  useEffect(() => {
    const loadFamilyInfo = async () => {
      if (!currentFamily?.id) return;
      try {
        setLoading(true);
        const res = await api.family.getFamilyDetail(currentFamily.id);
        if (res.code === 0 && res.data) {
          setFamilySettings({
            name: res.data.name,
            pointName: res.data.point_name,
            exchangeRatio: Number(res.data.exchange_ratio) || 5
          });
        }
      } catch (error) {
        console.error('Failed to load family info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFamilyInfo();
  }, [currentFamily?.id]);

  const handleSaveSettings = async () => {
    if (!currentFamily?.id) return;

    try {
      setSubmitting(true);
      const res = await api.family.updateFamily(currentFamily.id, {
        name: tempSettings.name,
        point_name: tempSettings.pointName,
        exchange_ratio: tempSettings.exchangeRatio
      });

      if (res.code === 0) {
        setFamilySettings(tempSettings);
        setShowFamilySettings(false);
        // 更新左侧菜单和家庭切换弹窗中的家庭名称
        updateCurrentFamilyName(tempSettings.name);
        // 刷新当前家庭信息
        if (res.data) {
          setFamilySettings({
            name: res.data.name,
            pointName: res.data.point_name,
            exchangeRatio: Number(res.data.exchange_ratio) || 5
          });
        }
      }
    } catch (error) {
      console.error('Failed to save family settings:', error);
      alert('保存失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 flex flex-col min-h-full">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-black text-blue-900">成员管理</h1>
            <p className="text-gray-400 text-sm mt-0.5">管理家庭成员与权限</p>
          </div>
        </div>

        <div className="flex gap-1 bg-blue-50 p-1 rounded-xl w-fit mb-5">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1">
          {activeTab === 'children' ? <ChildrenTab /> : <AdultsTab />}
        </div>

        {/* Family Settings Bottom Bar */}
        <div className="mt-6 pt-4 border-t border-blue-50">
          <button
            onClick={() => {
              if (!canManage) {
                alert('记录员无权修改家庭设置，仅管理员可操作');
                return;
              }
              setTempSettings(familySettings);
              setShowFamilySettings(true);
            }}
            className={`w-full flex items-center justify-between bg-white rounded-2xl px-5 py-4 border transition-all group ${
              canManage
                ? 'border-blue-100 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer'
                : 'border-gray-100 bg-gray-50 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 group-hover:scale-105 transition-transform">
                <i className="ri-settings-3-fill text-white text-base"></i>
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-blue-900">家庭设置</p>
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  {familySettings.name} · 积分单位：<i className={`${getPointsIcon(familySettings.pointName)} text-amber-400 text-[10px]`}></i>{familySettings.pointName} · 兑换比例：{familySettings.exchangeRatio}{familySettings.pointName}=1元
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-blue-500 font-medium hidden group-hover:block whitespace-nowrap">点击编辑</span>
              <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-blue-500 text-lg"></i>
            </div>
          </button>
        </div>
      </div>

      {/* Family Settings Modal */}
      {showFamilySettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowFamilySettings(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[560px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-700 rounded-t-2xl px-6 py-5 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/15">
                  <i className="ri-settings-3-fill text-white text-base"></i>
                </div>
                <div>
                  <h3 className="text-white font-bold">家庭设置</h3>
                  <p className="text-white/60 text-xs">配置家庭基础信息</p>
                </div>
              </div>
              <button onClick={() => setShowFamilySettings(false)} className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white cursor-pointer rounded-lg hover:bg-white/10">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <i className="ri-home-heart-fill text-rose-400"></i>
                  家庭名称
                </label>
                <input
                  value={tempSettings.name}
                  onChange={e => setTempSettings({ ...tempSettings, name: e.target.value })}
                  placeholder="例：张家、李氏大家庭"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                  <i className={`${getPointsIcon(tempSettings.pointName)} text-amber-400`}></i>
                  积分名称
                </label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {['星星', '金币', '友爱分', '彩虹豆', '勇气值', '钻石', '阳光', '月亮', '花朵', '皇冠', '火箭', '音符', '书本', '奖杯', '奖章'].map(n => (
                    <button
                      key={n}
                      onClick={() => setTempSettings({ ...tempSettings, pointName: n })}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                        tempSettings.pointName === n ? 'border-amber-400 text-amber-600 bg-amber-50' : 'border-gray-200 text-gray-500 hover:border-amber-200 hover:bg-amber-50/50'
                        }`}
                    >
                      <i className={`${getPointsIcon(n)} text-sm`}></i>
                      {n}
                    </button>
                  ))}
                </div>
                <input
                  value={tempSettings.pointName}
                  onChange={e => setTempSettings({ ...tempSettings, pointName: e.target.value })}
                  placeholder="或自定义积分名称"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <i className="ri-exchange-cny-fill text-emerald-500"></i>
                  积分兑换比例（{tempSettings.pointName}:人民币）
                </label>
                <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 flex-1">
                    <i className={`${getPointsIcon(tempSettings.pointName)} text-amber-400`}></i>
                    <span className="text-sm font-bold text-blue-900">{tempSettings.exchangeRatio}</span>
                    <span className="text-sm text-gray-500">{tempSettings.pointName}</span>
                  </div>
                  <i className="ri-exchange-fill text-gray-400"></i>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-500">¥ 1 元</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={tempSettings.exchangeRatio}
                    onChange={e => setTempSettings({ ...tempSettings, exchangeRatio: Number(e.target.value) })}
                    className="flex-1 accent-blue-500"
                  />
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={tempSettings.exchangeRatio}
                    onChange={e => setTempSettings({ ...tempSettings, exchangeRatio: Number(e.target.value) })}
                    className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400 text-center"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">当前设置：{tempSettings.exchangeRatio}个{tempSettings.pointName}可兑换1元人民币</p>
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white py-3 rounded-xl font-bold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? '保存中...' : '保存设置'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
